/**
 * Main-thread side of the workeredux bridge: Redux middleware that mirrors actions
 * into the Web Worker's store.
 *
 * Forwards actions tagged by markWorkerAction (src/utils/workeredux/client/actions.ts),
 * or all actions when postAll is true, to the worker via the actionBuffer dispatcher
 * (src/utils/workeredux/actionBuffer.ts). Actions received back from the worker (single
 * or batched arrays) are re-dispatched into the main store; worker errors are dispatched
 * as errorAction. The returned terminate() stops forwarding, clears the buffer interval,
 * and kills the worker. Message listeners are attached when the middleware is applied.
 *
 * It `return`s `next(action)` so the downstream dispatch result propagates back to the
 * caller. This matters because the middleware is prepended ahead of redux-thunk: dropping
 * the return value would make every `dispatch(thunk)` resolve to `undefined`, breaking
 * `putResolve`/`await dispatch(...)` consumers even when the thunk itself resolved data.
 */
import { isAction, MiddlewareAPI } from '@reduxjs/toolkit';
import { errorAction, isWorkerAction } from './actions';
import { actionBuffer } from '../actionBuffer';

/**
 * Function to create Redux Worker middleware.
 *
 * @param worker - the Worker instance.
 *
 * @param [postAll] - set to `true` to post all Actions to the Worker.
 *
 * @returns The `reduxWorkerMiddleware` and a `terminate` function.
 */
export function createReduxWorkerMiddleware(worker: Worker, postAll = false) {
  let isActive = true;
  const [dispatch, closeArrayBuffer] = actionBuffer(worker);

  const reduxWorkerMiddleware =
    (api: MiddlewareAPI) => (next: (value: unknown) => unknown) => {
      worker.addEventListener('message', (evt) => {
        Array.isArray(evt.data)
          ? evt.data.map((d) => {
              api.dispatch(d);
            })
          : api.dispatch(evt.data);
      });
      worker.addEventListener('error', (evt) => api.dispatch(errorAction(evt)));
      // return (action: Action) => {
      return (action: unknown) => {
        if (
          isActive &&
          isAction(action) &&
          (postAll || isWorkerAction(action))
        ) {
          dispatch(action);
        }
        return next(action);
      };
    };

  const terminate = () => {
    isActive = false;
    closeArrayBuffer();
    worker.terminate();
  };

  return {
    reduxWorkerMiddleware,
    terminate,
  };
}
